import axios from "axios";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { BlogAddSchema } from "../../../ValidationSchema";
import ReactSelect from "../../../Components/PortalSelect";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const colourStyles = {
  control: (styles) => ({
    ...styles,
    border: "1px solid #bfc9d4",
    boxShadow: "#000 !important",
    borderRadius: "6px",
    padding: "4px",
    "&:hover": {
      border: "1px solid #bfc9d4",
    },
  }),
  multiValue: (styles) => {
    return {
      ...styles,
      backgroundColor: "#606060",
    };
  },
  multiValueLabel: (styles) => ({
    ...styles,
    color: "#fff",
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: "#000",
    "& > svg": {
      backgroundColor: "#fff",
      borderRadius: "10px",
      marginLeft: "2px",
      marginRight: "2px",
    },
    ":hover": {
      backgroundColor: data.color,
      color: data.color,
    },
  }),
  option: (styles, { data }) => {
    return {
      ...styles,
      backgroundColor: "white",
      color: "#000",
      ":hover": {
        backgroundColor: "#f5f5f5",
      },
    };
  },
};

const BlogAdd = () => {
  const Navigate = useNavigate();
  const [initialValues] = useState({
    title: "",
    author_name: "",
    tags: "",
    author_image: null,
    description: "",
    category: "",
    image_path: null,
  });

  const [categoryData, setCategoryData] = useState([]);

  const CategoriesData = () => {
    axios
      .get(`${process.env.REACT_APP_URL}/admin/blog/category`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("Token")}`,
        },
      })
      .then((res) => {
        setCategoryData(
          res?.data?.data?.categories.map((item) => {
            return {
              label: item?.category || "Untitled", // fallback if title is undefined
              value: item?.blog_id,
            };
          })
        );
      })
      .catch((err) => {
        if (err.response.data.message === "Unauthorized!") {
          Navigate("/");
        }
      });
  };

  const AddBlog = useFormik({
    initialValues,
    validationSchema: BlogAddSchema,

    onSubmit: (values, action) => {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("category", values.category);
      formData.append("author_name", values.author_name);
      formData.append("author_image", values.author_image);
      formData.append("image_path", values.image_path);
      formData.append("date_time", values.date_time);
      formData.append("tags", values.tags);

      axios
        .post(`${process.env.REACT_APP_URL}/admin/blog`, formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("Token")}`,
          },
        })
        .then((res) => {
          if (res.status === 200) {
            toast.success(`${res?.data?.message}`);
            Navigate("/admin/blog", { state: res.data.message });
          }
        })
        .catch((err) => {
          toast.error(err.response.data.message);
        });
    },
  });

  //   const UserRole = () => {
  //     axios
  //       .get(`${process.env.REACT_APP_URL}/common/roles`)
  //       .then((res) => {
  //         setUserRole(res.data.roles);
  //       })
  //       .catch((err) => {
  //         if (err.response.data.message === "Unauthorized!") {
  //           Navigate("/admin/login");
  //         }
  //       });
  //   };

  useEffect(() => {
    CategoriesData();
  }, []);

  return (
    <div className="al">
      <Toaster position="top-center" reverseOrder={false} />

      <div className="al-head">
        <div>
          <h1 className="al-title">Add Blog</h1>
          <p className="al-sub">Create a new blog post.</p>
        </div>
        <div className="al-head__actions">
          <button type="button" className="al-btn" onClick={() => Navigate(-1)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
        </div>
      </div>

      <form onSubmit={AddBlog.handleSubmit}>
        <div className="al-card">
          <div className="al-cardhead">
            <div className="al-cardhead__icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg></div>
            <h3 className="al-cardhead__title">Blog Details</h3>
          </div>

          <div className="al-form">
            {/* Title */}
            <div>
              <label className="al-label">Title</label>
              <input className="al-input" type="text" placeholder="Enter Title"
                value={AddBlog.values.title} onChange={AddBlog.handleChange} onBlur={AddBlog.handleBlur} name="title" />
              {AddBlog.errors.title && AddBlog.touched.title && (
                <div className="al-err">{AddBlog.errors.title}</div>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="al-label">Category</label>
              <ReactSelect
                options={categoryData}
                isClearable={false}
                styles={colourStyles}
                components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                placeholder="Select Category"
                name="category"
                onChange={(e) => AddBlog.setFieldValue("category", e?.label)}
                value={categoryData.find((item) => item.label === AddBlog.values.category) || null}
              />
              {AddBlog.errors.category && AddBlog.touched.category && (
                <div className="al-err">{AddBlog.errors.category}</div>
              )}
            </div>

            {/* Author Name */}
            <div>
              <label className="al-label">Author Name</label>
              <input className="al-input" type="text" placeholder="Enter Author Name"
                value={AddBlog.values.author_name} onChange={AddBlog.handleChange} onBlur={AddBlog.handleBlur} name="author_name" />
              {AddBlog.errors.author_name && AddBlog.touched.author_name && (
                <div className="al-err">{AddBlog.errors.author_name}</div>
              )}
            </div>

            {/* Tags */}
            <div>
              <label className="al-label">Tags</label>
              <input className="al-input" type="text" placeholder="Enter Tags"
                value={AddBlog.values.tags} onChange={AddBlog.handleChange} onBlur={AddBlog.handleBlur} name="tags" />
              {AddBlog.errors.tags && AddBlog.touched.tags && (
                <div className="al-err">{AddBlog.errors.tags}</div>
              )}
            </div>

            {/* Author Image */}
            <div>
              <label className="al-label">Author Image</label>
              <input className="al-input al-input--file" type="file" name="author_image" accept="image/*"
                onChange={(e) => AddBlog.setFieldValue("author_image", e.target.files && e.target.files[0])}
                onBlur={AddBlog.handleBlur} />
              {AddBlog.errors.author_image && AddBlog.touched.author_image && (
                <div className="al-err">{AddBlog.errors.author_image}</div>
              )}
              <div className="mt-2">
                {AddBlog.values.author_image && typeof AddBlog.values.author_image === "string" && (
                  <img src={`${process.env.REACT_APP_IMAGE_URL}/uploads/${AddBlog.values.author_image}`} alt="Preview"
                    style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "8px" }} />
                )}
                {AddBlog.values.author_image && typeof AddBlog.values.author_image === "object" && (
                  <img src={URL.createObjectURL(AddBlog.values.author_image)} alt="New Upload Preview"
                    style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "8px" }} />
                )}
              </div>
            </div>

            {/* Picture */}
            <div>
              <label className="al-label">Picture</label>
              <input className="al-input al-input--file" type="file" name="image_path" accept="image/*"
                onChange={(e) => AddBlog.setFieldValue("image_path", e.target.files && e.target.files[0])}
                onBlur={AddBlog.handleBlur} />
              {AddBlog.errors.image_path && AddBlog.touched.image_path && (
                <div className="al-err">{AddBlog.errors.image_path}</div>
              )}
              <div className="mt-2">
                {AddBlog.values.image_path && typeof AddBlog.values.image_path === "string" && (
                  <img src={`${process.env.REACT_APP_IMAGE_URL}/uploads/${AddBlog.values.image_path}`} alt="Preview"
                    style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "8px" }} />
                )}
                {AddBlog.values.image_path && typeof AddBlog.values.image_path === "object" && (
                  <img src={URL.createObjectURL(AddBlog.values.image_path)} alt="New Upload Preview"
                    style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "8px" }} />
                )}
              </div>
            </div>

            {/* Date */}
            <div>
              <label className="al-label">Date</label>
              <input className="al-input" type="date" placeholder="Enter Date"
                value={AddBlog.values.date_time} onChange={AddBlog.handleChange} onBlur={AddBlog.handleBlur} name="date_time" />
              {AddBlog.errors.date_time && AddBlog.touched.date_time && (
                <div className="al-err">{AddBlog.errors.date_time}</div>
              )}
            </div>

            {/* Description (full width) */}
            <div className="al-form__full">
              <label className="al-label">Description</label>
              <CKEditor
                editor={ClassicEditor}
                data={AddBlog.values.description}
                className="custom-ckeditor"
                onChange={(event, editor) => {
                  const data = editor.getData();
                  AddBlog.setFieldValue("description", data);
                }}
                onBlur={() => AddBlog.setFieldTouched("description", true)}
              />
              {AddBlog.errors.description && AddBlog.touched.description && (
                <div className="al-err">{AddBlog.errors.description}</div>
              )}
            </div>
          </div>

          <div className="al-actionbar">
            <button type="button" className="al-btn" onClick={() => Navigate(-1)}>Cancel</button>
            <button type="submit" className="al-btn al-btn--primary">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              Create Blog
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BlogAdd;
